import { Box, IconButton } from '@chakra-ui/react'
import { DataTypeLocator } from '@helo/cms'
import { forwardRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { StringFilterInput } from '../../../gql/graphql'
import { RouteConfig } from '../../../router/routes'
import { ViewUrlState } from '../../../store/appStore/view/viewSlice.types'
import { Icon } from '../../Icons/Icon'
import { SearchIcon } from '../../Icons/SearchIcon'
import { BaseInput, BaseInputProps } from '../BaseInput'

export type SearchTextInputProps = BaseInputProps & {
  route: RouteConfig
  filterKey: string
  queryId: string
  querySelector: DataTypeLocator
}

/**
 * Converts input value to filter input format.
 * Auto-selects condition based on input value:
 * - If contains comma(s) → use "In List" (exact matches from comma-separated list)
 * - Otherwise → use "Contains" (partial match)
 */
const valueToFilterInput = (value: string): StringFilterInput => {
  const trimmedValue = value.trim()
  const hasComma = trimmedValue.includes(',')

  if (hasComma) {
    // "In List" - split by comma, trim each value, filter empty strings
    const values = trimmedValue
      .split(',')
      .map(v => v.trim())
      .filter(v => v.length > 0)
    return {
      in: values,
      mode: 'insensitive',
    }
  }

  // "Contains" - partial match
  return {
    contains: trimmedValue,
    mode: 'insensitive',
  }
}

export const SearchTextInput = forwardRef<
  HTMLInputElement,
  SearchTextInputProps
>(
  (
    {
      route,
      filterKey,
      queryId,
      querySelector: _querySelector,
      placeholder,
      ...props
    },
    ref,
  ) => {
    const [inputValue, setInputValue] = useState('')

    const navigate = useNavigate()

    const handleSubmit = () => {
      const trimmedValue = inputValue.trim()
      if (!trimmedValue) return

      // Build the filter input using the same format as TextFilterForm
      const filterInput = valueToFilterInput(trimmedValue)

      // Build URL state with the filter in the variables.filter structure
      // (matches the format used by the filter editor)
      const viewUrlState: ViewUrlState = {
        queryStates: [
          {
            queryId: queryId,
            variables: {
              filter: {
                [filterKey]: filterInput,
              },
            },
          },
        ],
      }

      const searchParams = new URLSearchParams({
        viewState: JSON.stringify(viewUrlState),
      })

      navigate(`${route.path}?${searchParams.toString()}`)
    }

    return (
      <form
        onSubmit={event => {
          event.preventDefault()
          handleSubmit()
        }}
      >
        <BaseInput
          ref={ref}
          type="text"
          variant="search"
          value={inputValue}
          placeholder={placeholder}
          onChange={event => {
            setInputValue(event.target.value)
          }}
          leftInputElement={
            <Box paddingLeft="8px">
              <Icon
                icon={route?.routeIconName!}
                boxSize="20px"
                color="presets.neutral.0"
                opacity="0.6"
              />
            </Box>
          }
          rightInputElement={
            <IconButton
              aria-label="Search"
              icon={<SearchIcon color="presets.neutral.0" boxSize="18px" />}
              bg="presets.turquoise.40"
              borderRadius="full"
              boxSize="32px"
              onClick={handleSubmit}
            />
          }
          {...props}
        />
      </form>
    )
  },
)
